import { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { Droplet, TrendingDown, Clock, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import type { TankStats } from '../../services/sensorDevices';

Chart.register(...registerables);

function formatLiters(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  return Math.round(v).toLocaleString() + ' L';
}

function formatDays(d: number | null): string {
  if (d === null || !Number.isFinite(d)) return '—';
  if (d >= 365) return '> 1 year';
  if (d >= 60) return Math.round(d / 7) + ' weeks';
  if (d < 1) return Math.round(d * 24) + ' hours';
  return d.toFixed(1) + ' days';
}

interface TankVolumeCardProps {
  stats: TankStats;
  title?: string;
  depthMm?: number | null;
  readingTime?: string | null;
  online?: boolean;
}

export default function TankVolumeCard({ stats, title, depthMm, readingTime, online }: TankVolumeCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const pct = stats.percent_full ?? 0;
  const low = pct !== null && pct < 15;
  const depleting = stats.days_to_empty !== null && stats.days_to_empty < 7;

  useEffect(() => {
    if (!canvasRef.current) return;
    const usage = stats.daily_usage || [];
    if (usage.length === 0) {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      return;
    }

    const last14 = usage.slice(-14);
    const labels = last14.map(u => u.date.slice(5)); // MM-DD
    const data = last14.map(u => u.liters);

    if (chartRef.current) {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = data;
      chartRef.current.update();
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Daily usage (L)',
          data,
          backgroundColor: '#2563eb',
          borderRadius: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { font: { size: 10 } } },
          y: { beginAtZero: true, ticks: { font: { size: 10 } } },
        },
      },
    });

    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  }, [stats]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Droplet className="w-4 h-4 text-blue-600" />
            {title || 'Tank volume'}
          </span>
          {online !== undefined && (
            <span className={`text-xs font-normal inline-block px-2 py-0.5 rounded-full ${
              online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {online ? 'Online' : 'Offline'}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {/* Headline + fill bar */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <div className="text-3xl font-bold text-gray-800">
                {formatLiters(stats.current_liters)}
              </div>
              <div className="text-xs text-gray-500">
                of {formatLiters(stats.capacity_liters)} capacity
              </div>
            </div>
            <div className={`text-2xl font-semibold ${low ? 'text-red-600' : 'text-blue-600'}`}>
              {stats.percent_full !== null ? stats.percent_full.toFixed(0) + '%' : '—'}
            </div>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${low ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
          </div>
          {(depthMm !== undefined || readingTime) && (
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
              {depthMm !== undefined && depthMm !== null && (
                <span>Depth: <span className="font-medium text-gray-700">{Number(depthMm).toFixed(0)} mm</span></span>
              )}
              {readingTime && (
                <span className="ml-auto">{new Date(readingTime).toLocaleString()}</span>
              )}
            </div>
          )}
        </div>

        {/* Trend stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <TrendingDown className="w-3.5 h-3.5" /> Avg daily use (7d)
            </div>
            <div className="text-lg font-semibold text-gray-800">
              {formatLiters(stats.avg_daily_usage_liters)}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${depleting ? 'bg-amber-50' : 'bg-gray-50'}`}>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              {depleting ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> : <Clock className="w-3.5 h-3.5" />}
              Days to empty
            </div>
            <div className={`text-lg font-semibold ${depleting ? 'text-amber-700' : 'text-gray-800'}`}>
              {formatDays(stats.days_to_empty)}
            </div>
          </div>
        </div>

        {/* Usage chart */}
        <div>
          <div className="text-xs text-gray-500 mb-1">Daily usage (last 14 days)</div>
          <div className="h-32">
            {(stats.daily_usage || []).length === 0 ? (
              <div className="text-sm text-gray-400 italic">Not enough history yet.</div>
            ) : (
              <canvas ref={canvasRef} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
